import { withAuthenticationRequired } from "@auth0/auth0-react";
import { store } from "../store";
import { useTranslation } from "react-i18next";
import { Grid, Paper, Box, Typography, Select, MenuItem, FormControl, InputLabel } from "@mui/material";
import { alpha } from "@mui/material/styles";
import ResidencesTable from "../components/tables/Residences";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Home, People, Store, FilterList } from "@mui/icons-material";
import fetchData from "../API/Fetch";

function Residences(props) {
  const { t } = useTranslation("common");

  const userDB = useSelector((state) => state.userDB);
  const residences = useSelector((state) => state.residences);
  const establishments = useSelector((state) => state.establishments);
  const zones = useSelector((state) => state.zones);

  const [selectedEstablishment, setSelectedEstablishment] = useState("all");
  const [selectedZone, setSelectedZone] = useState("all");
  const [availableZones, setAvailableZones] = useState([]);

  useEffect(() => {
    store.dispatch({
      type: "pageTitle/update",
      payload: t("common.residences"),
    });

    let pageActions = [];

    if (userDB && userDB.admin) {
      pageActions = [
        {
          name: t("common.add_new", { name: "" }),
          icon: "Add",
          url: "/add/residence",
        },
      ];
    }

    store.dispatch({ type: "pageActions/update", payload: pageActions });
  }, [t, userDB]);

  // Charger les zones quand l'établissement change
  useEffect(() => {
    const loadZones = async () => {
      if (selectedEstablishment && selectedEstablishment !== "all") {
        try {
          const zonesData = await fetchData(`/zones/establishment/${selectedEstablishment}`);
          setAvailableZones(zonesData || []);
        } catch (error) {
          setAvailableZones([]);
        }
      } else {
        // Si "Tous" est sélectionné, utiliser toutes les zones du store
        setAvailableZones(zones || []);
      }
      // Reset le filtre zone quand l'établissement change
      setSelectedZone("all");
    };
    loadZones();
  }, [selectedEstablishment, zones]);

  // Filtrer les résidences par établissement et zone
  let filteredResidences = residences || [];

  if (selectedEstablishment !== "all") {
    filteredResidences = filteredResidences.filter(res => res.establishment_id === selectedEstablishment);
  }

  if (selectedZone !== "all") {
    filteredResidences = filteredResidences.filter(res => res.zone_id === selectedZone);
  }

  // Calcul des statistiques
  const totalResidences = filteredResidences?.length || 0;
  const totalOccupied = filteredResidences?.filter(res => res.current_occupation > 0).length || 0;
  const totalCapacity = filteredResidences?.reduce((acc, res) => acc + (res.capacity || 0), 0) || 0;

  const statsCards = [
    { title: t("common.total"), value: totalResidences, icon: Home, color: "#78a882" },
    { title: t("common.occupied") || "Occupées", value: totalOccupied, icon: People, color: "#6b9dad" },
    { title: t("common.capacity"), value: totalCapacity, icon: Store, color: "#c4a574" },
  ];

  return (
    <Grid container spacing={2}>
      {/* Cartes KPI */}
      <Grid item xs={12}>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
          {statsCards.map((card, index) => (
            <Paper
              key={index}
              sx={{
                p: 2,
                flex: "1 1 200px",
                minWidth: 200,
                background: alpha(card.color, 0.15),
                borderLeft: `4px solid ${card.color}`,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <card.icon sx={{ color: card.color, fontSize: 32 }} />
                <Box>
                  <Typography variant="h4" sx={{ color: card.color, fontWeight: "bold" }}>
                    {card.value}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {card.title}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          ))}
        </Box>
      </Grid>

      {/* Filtre par établissement */}
      <Grid item xs={12}>
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <FilterList sx={{ color: "#78a882" }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
              {t("common.title_filters")}
            </Typography>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>{t("common.establishment")}</InputLabel>
              <Select
                value={selectedEstablishment}
                label={t("common.establishment")}
                onChange={(e) => setSelectedEstablishment(e.target.value)}
              >
                <MenuItem value="all">{t("common.all")}</MenuItem>
                {establishments?.map((establishment) => (
                  <MenuItem key={establishment.id} value={establishment.id}>
                    {establishment.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Zone</InputLabel>
              <Select
                value={selectedZone}
                label="Zone"
                onChange={(e) => setSelectedZone(e.target.value)}
                disabled={availableZones.length === 0}
              >
                <MenuItem value="all">{t("common.all")}</MenuItem>
                {availableZones?.map((zone) => (
                  <MenuItem key={zone.id} value={zone.id}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: "50%",
                          backgroundColor: zone.color || "#c4a574",
                        }}
                      />
                      {zone.name}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Paper>
      </Grid>

      {/* Tableau */}
      <Grid item xs={12}>
        <ResidencesTable
          filteredResidences={filteredResidences}
          availableZones={availableZones}
          selectedEstablishment={selectedEstablishment}
        />
      </Grid>
    </Grid>
  );
}

export default withAuthenticationRequired(Residences);
